import api from './api'
import type {
  Workflow,
  WorkflowVersion,
  WorkflowVersionListResponse,
  WorkflowDiff,
  WorkflowImportResponse,
  PublishRequest,
} from '@/schemas/workflow'
import type { WorkflowExport } from '@/schemas/workflow-export'

/**
 * Typed API client for Phase 03 workflow lifecycle endpoints (03-01/02/03 backends).
 *
 * URL templates match backend routes exactly:
 *   POST   /projects/{pid}/workflows/{wid}/publish
 *   POST   /projects/{pid}/workflows/{wid}/unpublish
 *   GET    /projects/{pid}/workflows/{wid}/versions
 *   GET    /projects/{pid}/workflows/{wid}/versions/{n}
 *   POST   /projects/{pid}/workflows/{wid}/versions/{n}/restore
 *   GET    /projects/{pid}/workflows/{wid}/export
 *   GET    /projects/{pid}/workflows/{wid}/versions/{n}/export
 *   POST   /projects/{pid}/workflows/import
 *   GET    /projects/{pid}/workflows/{wid}/diff?from&to
 */
export const workflowLifecycleService = {
  async publish(
    projectId: string,
    workflowId: string,
    data: PublishRequest
  ): Promise<WorkflowVersion> {
    const res = await api.post<WorkflowVersion>(
      `/projects/${projectId}/workflows/${workflowId}/publish`,
      data
    )
    return res.data
  },

  async unpublish(projectId: string, workflowId: string): Promise<Workflow> {
    const res = await api.post<Workflow>(
      `/projects/${projectId}/workflows/${workflowId}/unpublish`
    )
    return res.data
  },

  async listVersions(
    projectId: string,
    workflowId: string,
    params?: { page?: number; page_size?: number }
  ): Promise<WorkflowVersionListResponse> {
    const res = await api.get<WorkflowVersionListResponse>(
      `/projects/${projectId}/workflows/${workflowId}/versions`,
      { params }
    )
    return res.data
  },

  async getVersion(
    projectId: string,
    workflowId: string,
    versionNumber: number
  ): Promise<WorkflowVersion> {
    const res = await api.get<WorkflowVersion>(
      `/projects/${projectId}/workflows/${workflowId}/versions/${versionNumber}`
    )
    return res.data
  },

  async restore(
    projectId: string,
    workflowId: string,
    versionNumber: number
  ): Promise<Workflow> {
    const res = await api.post<Workflow>(
      `/projects/${projectId}/workflows/${workflowId}/versions/${versionNumber}/restore`
    )
    return res.data
  },

  async exportWorkflow(
    projectId: string,
    workflowId: string
  ): Promise<WorkflowExport> {
    const res = await api.get<WorkflowExport>(
      `/projects/${projectId}/workflows/${workflowId}/export`
    )
    return res.data
  },

  async exportVersion(
    projectId: string,
    workflowId: string,
    versionNumber: number
  ): Promise<WorkflowExport> {
    const res = await api.get<WorkflowExport>(
      `/projects/${projectId}/workflows/${workflowId}/versions/${versionNumber}/export`
    )
    return res.data
  },

  async importWorkflow(
    projectId: string,
    payload: WorkflowExport
  ): Promise<WorkflowImportResponse> {
    const res = await api.post<WorkflowImportResponse>(
      `/projects/${projectId}/workflows/import`,
      payload
    )
    return res.data
  },

  async diff(
    projectId: string,
    workflowId: string,
    fromVersion: number,
    toVersion: number
  ): Promise<WorkflowDiff> {
    const res = await api.get<WorkflowDiff>(
      `/projects/${projectId}/workflows/${workflowId}/diff`,
      { params: { from: fromVersion, to: toVersion } }
    )
    return res.data
  },
}
